/**
 * Sidebar — barre latérale (80px) avec liens de navigation (icône + libellé).
 *
 * @remarks
 * Rend les liens **agrégés depuis le registry** (`getAllNavigationLinks`).
 * **N'importe aucune feature directement** — elle itère sur ce que les
 * features ont déclaré dans leurs `manifest.navigation`.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { AppContext, Context } from '@/app/registry';
import { featureRegistry } from '@/app/registry';
import { useMyUserTests } from '@/features/tech-tests';
import { splitNavigationLinks } from './navigation-links';

type NavLinkData = ReturnType<typeof featureRegistry.getAllNavigationLinks>[number];

interface Props {
  context: Context;
  appCtx: AppContext;
}

export function Sidebar({ context, appCtx }: Props) {
  // `appCtx` est memoizé en amont (DesktopShell/TabletShell/MobileShell), donc
  // on n'invalide ce calcul que si user/flags changent — pas à chaque render.
  const { topLinks, bottomLinks } = useMemo(
    () => splitNavigationLinks(featureRegistry.getAllNavigationLinks(appCtx)),
    [appCtx],
  );

  return (
    <aside
      aria-label="Navigation principale"
      className="relative flex h-full w-20 shrink-0 flex-col bg-transparent after:pointer-events-none after:absolute after:inset-y-0 after:-right-0.5 after:w-1 after:bg-gradient-to-r after:from-transparent after:via-black/[0.02] after:to-transparent"
    >
      {/* Le défilement vit sur ce conteneur interne, pas sur l'`aside` : un
          `overflow` sur l'`aside` rognerait l'ombre portée posée en `after:` à
          sa droite. `min-h-0` laisse le conteneur rétrécir sous la hauteur de
          ses liens ; sur un écran peu haut, les liens du bas restent donc
          atteignables en défilant, au lieu d'être rognés par le shell
          (`overflow-hidden`). Sur un écran haut, `justify-between` les garde
          ancrés en bas. */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-between gap-2 overflow-y-auto overscroll-contain py-2 [scrollbar-width:thin]">
        <nav className="flex w-full flex-col items-center gap-2">
          {topLinks.map((link) => (
            <SidebarLink key={link.id} link={link} context={context} />
          ))}
        </nav>

        {bottomLinks.length > 0 ? (
          <nav className="flex w-full flex-col items-center gap-2">
            {bottomLinks.map((link) => (
              <SidebarLink key={link.id} link={link} context={context} />
            ))}
          </nav>
        ) : null}
      </div>
    </aside>
  );
}

function SidebarLink({ link, context }: { link: NavLinkData; context: Context }) {
  // Seul le lien marqué `pulse` souscrit à la query user-tests — les autres
  // liens ne déclenchent aucune requête.
  if (link.pulse) return <PulsingSidebarLink link={link} context={context} />;
  return <SidebarLinkContent link={link} context={context} pulsing={false} />;
}

/**
 * Variante d'un lien `pulse` (marketplace) : pulse tant que l'utilisateur ne
 * possède aucune évaluation. Dès que `/user-tests` renvoie au moins un élément,
 * le pulse disparaît. Pas de pulse pendant le chargement (évite un flash).
 */
function PulsingSidebarLink({ link, context }: { link: NavLinkData; context: Context }) {
  const { data: tests, isPending } = useMyUserTests();
  const pulsing = !isPending && (tests?.length ?? 0) === 0;
  return <SidebarLinkContent link={link} context={context} pulsing={pulsing} />;
}

function SidebarLinkContent({
  link,
  context,
  pulsing,
}: {
  link: NavLinkData;
  context: Context;
  pulsing: boolean;
}) {
  const Icon = link.icon;
  // Pas de `NavLink` : son match par préfixe d'URL allumait « Test technique »
  // sur `/tests/:testId/evaluation/:eid/attempt/:aid`, une route qui appartient
  // à `candidate-results`. L'état actif se lit sur le contexte applicatif.
  const isActive = link.activeContexts.includes(context);

  const content = (
    <>
      <span className="relative inline-flex size-9 items-center justify-center">
        {pulsing && (
          <>
            <span
              aria-hidden
              className="border-primary-500 pointer-events-none absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border-2"
            />
            <span
              aria-hidden
              className="bg-primary-500/25 pointer-events-none absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full [animation-delay:600ms]"
            />
          </>
        )}
        <Icon className={pulsing ? 'text-primary relative size-5' : 'size-5'} aria-hidden />
      </span>
      {/* Libellé sous l'icône — hérite de la couleur d'état du NavLink.
          Police de marque (Schibsted) = titres de navigation. */}
      <span className="font-brand line-clamp-2 text-center text-xs leading-tight">
        {link.label}
      </span>
    </>
  );

  // Lien visible mais désactivé (ex: feature pas encore stabilisée, cf.
  // SAAS-1046) : pas de `to` → aucune navigation possible au clic. `role="link"`
  // + `aria-disabled` gardent le lien focusable et annoncé par les lecteurs
  // d'écran (plutôt que de le retirer du tab order avec un vrai `disabled`),
  // pour rester cohérent avec « visible mais grisé ». Le `guard` posé sur la
  // route protège l'accès réel — ce grisage n'est que visuel.
  if (link.disabled) {
    return (
      <span
        role="link"
        aria-disabled="true"
        aria-label={link.label}
        title={link.disabledTooltip ?? link.label}
        tabIndex={0}
        className="text-fg-muted relative flex w-full cursor-not-allowed flex-col items-center gap-1 rounded-md px-1 py-1.5 opacity-50"
      >
        {content}
      </span>
    );
  }

  return (
    <Link
      to={link.to}
      data-tour={`nav-${link.id}`}
      // `title` natif : tooltip plein-texte au survol (filet si le libellé déborde
      // sur >2 lignes) ; `aria-label` : nom accessible pour lecteurs d'écran.
      title={link.label}
      aria-label={link.label}
      aria-current={isActive ? 'page' : undefined}
      className={[
        'relative flex w-full flex-col items-center gap-1 rounded-md px-1 py-1.5 transition-colors',
        // Indicateur 2px bleu à gauche pour l'état actif (style V1).
        'before:bg-primary before:absolute before:top-1/4 before:left-0 before:h-1/2 before:w-0.5 before:rounded-r-sm before:opacity-0 before:transition-opacity',
        isActive
          ? 'text-primary bg-(--color-accent-bg) before:opacity-60 hover:bg-(--color-accent-bg)'
          : 'text-fg-muted hover:text-fg hover:bg-bg-hover',
      ].join(' ')}
    >
      {content}
    </Link>
  );
}
